import { fetchWithTokenRefresh } from "./withTokenRefresh";
import { parseResponseBody, resolveApiUrl } from "./api";

/**
 * Fetch a single message/pitch attachment's signed, expiring Cloudinary URL + metadata.
 * @param {number|string} attachmentId - The Interact::Attachment id
 * @returns {Promise<{data?: object, error?: object}>}
 */
const find = async (attachmentId) => {
  const response = await fetchWithTokenRefresh(resolveApiUrl(`/interact/api/attachments/${attachmentId}`), {
    method: "GET",
  });
  const body = await parseResponseBody(response);

  if (response.ok) {
    return { data: body?.data };
  }

  return { error: { status: response.status, statusText: response.statusText, body } };
};

/**
 * Fetch only a video attachment's transcript status/segments. Lean vs find(): no signed-URL
 * regeneration and no usage tracking, so it's cheap to poll while transcription is in progress.
 * @param {number|string} attachmentId - The Interact::Attachment id
 * @returns {Promise<{data?: {transcript: object}, error?: object}>}
 */
const transcriptStatus = async (attachmentId) => {
  const response = await fetchWithTokenRefresh(
    resolveApiUrl(`/interact/api/attachments/${attachmentId}/transcript_status`),
    {
      method: "GET",
    },
  );
  const body = await parseResponseBody(response);

  if (response.ok) {
    return { data: body?.data };
  }

  return { error: { status: response.status, statusText: response.statusText, body } };
};

/**
 * Record an engagement "view" for a video attachment when the user plays it. The signed-URL fetch
 * (find) deliberately does not track a video view server-side, so the player calls this on
 * tap-to-play. Fire-and-forget: the caller ignores the result.
 * @param {number|string} attachmentId - The Interact::Attachment id
 * @returns {Promise<{ok: true} | {error: object}>}
 */
const trackView = async (attachmentId) => {
  const response = await fetchWithTokenRefresh(resolveApiUrl(`/interact/api/attachments/${attachmentId}/track_view`), {
    method: "POST",
  });

  if (response.ok) {
    return { ok: true };
  }

  return { error: { status: response.status, statusText: response.statusText } };
};

export default { find, transcriptStatus, trackView };
